<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    <!-- Top Header -->
    <header class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 shadow-sm backdrop-blur-md bg-opacity-95">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <img src="@/images/logo/Ecole Ronsard.png" alt="Logo" class="h-12 w-auto">
          <div class="hidden sm:block">
            <h1 class="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">Ecole Ronsard ERP</h1>
            <p class="text-xs lg:text-sm text-gray-500 dark:text-gray-400">Central Administration</p>
          </div>
        </div>

        <div class="flex items-center gap-3 lg:gap-6">
          <!-- Search -->
          <div class="relative hidden md:block">
            <input 
              type="text" 
              placeholder="Search across modules..." 
              class="w-64 lg:w-80 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full py-2.5 pl-11 pr-4 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            >
            <svg class="absolute left-4 top-3 h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <!-- Dark Mode Toggle -->
          <button 
            @click="toggleDarkMode"
            class="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition"
          >
            <svg v-if="!isDarkMode" class="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
            <svg v-else class="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>

          <!-- Notifications -->
          <button class="relative p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition">
            <svg class="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span class="absolute top-1.5 right-1.5 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold ring-2 ring-white dark:ring-gray-800">3</span>
          </button>

          <!-- User Profile -->
          <div class="flex items-center gap-3 pl-3 lg:pl-6 border-l border-gray-200 dark:border-gray-700">
            <div class="hidden lg:block text-right">
              <p class="font-semibold text-sm text-gray-900 dark:text-white">{{ user?.name || 'Administrator' }}</p>
              <p class="text-xs text-emerald-600 dark:text-emerald-400">Super Admin</p>
            </div>
            <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl flex items-center justify-center font-bold text-sm shadow-md">
              {{ user?.name?.charAt(0) || 'A' }}
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
      <!-- Welcome Section -->
      <div class="mb-10 lg:mb-12">
        <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h2 class="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
              Welcome back, {{ user?.name?.split(' ')[0] || 'Admin' }}! 👋
            </h2>
            <p class="text-gray-600 dark:text-gray-400 mt-2 text-base lg:text-lg">
              Manage all departments and modules from this central portal
            </p>
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400">
            {{ currentDate }}
          </div>
        </div>
      </div>

      <!-- Departments Section -->
      <div class="mb-6 lg:mb-8">
        <h3 class="text-xl lg:text-2xl font-semibold text-gray-800 dark:text-white">Departments & Modules</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Select a department to access its management tools</p>
      </div>

      <!-- Modules Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 lg:gap-8">
        
        <ModuleCard
          title="Administration"
          description="System overview, user control and full platform access"
          icon="🛠️"
          color="violet"
          route="/admin-dashboard"
        />

        <ModuleCard
          title="Human Resources"
          description="Staff management, payroll, leave, recruitment and HR operations"
          icon="👥"
          color="secondary"
          route="/hrdashboard"
        />

        <ModuleCard
          title="Academics"
          description="Teachers management, timetable, subjects, exams and academic records"
          icon="👨‍🏫"
          color="blue"
          route="/academics-dashboard"
        />

        <ModuleCard
          title="Information Technology"
          description="System maintenance, support tickets, hardware, software and network"
          icon="💻"
          color="slate"
          route="/it-dashboard"
        />

        <ModuleCard
          title="Communication"
          description="Internal messaging, announcements, notices and parent communication"
          icon="📢"
          color="emerald"
          route="/com-dashboard"
        />
        <ModuleCard
  title="Administration"
  description="Manage administrative works, protocols, and central operations"
  icon="🏛️"
  color="indigo"
  route="/administration-dashboard"
/>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ModuleCard from '@/components/admin/ModuleCard.vue'

const user = ref({})
const isDarkMode = ref(false)

const currentDate = computed(() => {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
})

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
    localStorage.theme = 'dark'
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.theme = 'light'
  }
}

onMounted(() => {
  const savedUser = localStorage.getItem('user')
  if (savedUser) {
    user.value = JSON.parse(savedUser)
  }
  
  // Initialize dark mode from localStorage
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true
    document.documentElement.classList.add('dark')
  }
})
</script>